import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import {
    FCanvasComponent,
    FCreateConnectionEvent,
    FFlowModule,
    FMoveNodesEvent,
    FReassignConnectionEvent,
    FSelectionChangeEvent
} from '@foblex/flow';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import {
    WorkflowCustomerOption,
    WorkflowDefinition,
    WorkflowRoleOption,
    WorkflowResourceType,
    WorkflowStatusNode,
    WorkflowTransition,
    WorkflowTransitionCondition
} from '../workflow.model';
import { WorkflowService } from '../workflow.service';

type InspectorMode = 'workflow' | 'step' | 'action' | 'condition' | 'empty';

interface WorkflowGraphNode {
    node: WorkflowStatusNode;
    id: string;
    inputId: string;
    outputId: string;
    position: { x: number; y: number };
}

interface WorkflowGraphConnection {
    transition: WorkflowTransition;
    id: string;
    outputId: string;
    inputId: string;
}

interface ValidationMessage {
    severity: 'error' | 'warn';
    text: string;
}

@Component({
    selector: 'app-workflow-admin',
    standalone: true,
    templateUrl: './workflow-admin.component.html',
    styleUrl: './workflow-admin.component.css',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FFlowModule,
        AutoCompleteModule,
        ButtonModule,
        CheckboxModule,
        DialogModule,
        DividerModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        ProgressBarModule,
        SelectModule,
        TableModule,
        TabsModule,
        TagModule,
        TextareaModule,
        ToastModule,
        TooltipModule
    ],
    providers: [MessageService]
})
export class WorkflowAdminComponent implements OnInit {
    @ViewChild(FCanvasComponent) canvas?: FCanvasComponent;
    private readonly reviewTabKeys = ['steps', 'actions', 'checks'] as const;
    private readonly layoutStartX = 80;
    private readonly layoutStartY = 80;
    private readonly layoutColumnWidth = 420;
    private readonly layoutRowHeight = 220;
    private readonly layoutGridColumns = 4;

    companyId?: number;
    resourceTypes: WorkflowResourceType[] = [];
    definitions: WorkflowDefinition[] = [];
    roleOptions: WorkflowRoleOption[] = [];
    customerOptions: WorkflowCustomerOption[] = [];
    filteredCustomerOptions: WorkflowCustomerOption[] = [];
    selectedDefinition?: WorkflowDefinition;
    selectedTransition?: WorkflowTransition;
    selectedCondition?: WorkflowTransitionCondition;
    selectedNode?: WorkflowStatusNode;
    isLoading = false;
    workflowFilter = '';
    inspectorMode: InspectorMode = 'empty';
    graphNodes: WorkflowGraphNode[] = [];
    graphConnections: WorkflowGraphConnection[] = [];
    validationMessages: ValidationMessage[] = [];
    activeReviewTab: 'steps' | 'actions' | 'checks' = 'steps';

    resourceForm: FormGroup;
    definitionForm: FormGroup;
    nodeForm: FormGroup;
    transitionForm: FormGroup;
    conditionForm: FormGroup;

    showResourceDialog = false;

    readonly nodeTypes = ['START', 'NORMAL', 'APPROVAL', 'FINAL'].map(value => ({ label: this.toTitle(value), value }));
    readonly conditionTypes = ['ALWAYS', 'FIELD_RULE', 'ROLE_RULE', 'APPROVAL_RULE'].map(value => ({ label: this.toTitle(value), value }));

    constructor(
        private readonly fb: FormBuilder,
        private readonly workflowService: WorkflowService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly messageService: MessageService,
        private readonly route: ActivatedRoute,
        private readonly router: Router
    ) {
        this.resourceForm = this.fb.group({
            resourceTypeId: [null],
            resourceKey: ['', Validators.required],
            displayName: ['', Validators.required],
            detailRouteTemplate: [''],
            active: [true]
        });
        this.definitionForm = this.fb.group({
            workflowDefinitionId: [null],
            resourceKey: ['', Validators.required],
            name: ['', Validators.required],
            version: [1, Validators.required],
            initialStatusCode: ['', Validators.required],
            active: [true]
        });
        this.nodeForm = this.fb.group({
            nodeId: [null],
            statusCode: ['', Validators.required],
            label: ['', Validators.required],
            nodeType: ['NORMAL', Validators.required],
            sortOrder: [null],
            positionX: [null],
            positionY: [null],
            isFinal: [false],
            active: [true]
        });
        this.transitionForm = this.fb.group({
            transitionId: [null],
            fromStatusCode: ['', Validators.required],
            toStatusCode: ['', Validators.required],
            actionCode: ['', Validators.required],
            label: ['', Validators.required],
            requiresComment: [false],
            requiresAttachment: [false],
            allowedRoleId: [null],
            allowedCustomer: [null],
            active: [true]
        });
        this.conditionForm = this.fb.group({
            conditionId: [null],
            conditionType: ['ALWAYS', Validators.required],
            conditionJson: [''],
            errorMessage: [''],
            sortOrder: [null],
            active: [true]
        });
    }

    ngOnInit(): void {
        this.companyId = this.sessionStorageService.getCompany()?.id;
        this.route.queryParamMap.subscribe(params => {
            this.activeReviewTab = this.resolveReviewTab(params.get('tab'));
        });
        this.load();
    }

    setActiveReviewTab(value: string | number): void {
        const resolvedTab = this.resolveReviewTab(String(value));
        this.activeReviewTab = resolvedTab;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: resolvedTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    get filteredDefinitions(): WorkflowDefinition[] {
        const term = this.workflowFilter.trim().toLowerCase();
        if (!term) {
            return this.definitions;
        }
        return this.definitions.filter(item =>
            item.name.toLowerCase().includes(term) ||
            item.resourceKey.toLowerCase().includes(term)
        );
    }

    get activeNodes(): WorkflowStatusNode[] {
        return (this.selectedDefinition?.nodes || []).filter(node => node.active !== false);
    }

    get nodeOptions() {
        return this.activeNodes.map(node => ({ label: `${node.label} (${node.statusCode})`, value: node.statusCode }));
    }

    load(): void {
        if (!this.companyId) {
            return;
        }
        this.isLoading = true;
        forkJoin({
            resourceTypes: this.workflowService.getResourceTypes(this.companyId),
            definitions: this.workflowService.getDefinitions(this.companyId),
            roleOptions: this.workflowService.getRoleOptions(this.companyId),
            customerOptions: this.workflowService.getCustomerOptions(this.companyId)
        }).subscribe({
            next: ({ resourceTypes, definitions, roleOptions, customerOptions }) => {
                this.resourceTypes = resourceTypes;
                this.definitions = definitions;
                this.roleOptions = roleOptions;
                this.customerOptions = customerOptions;
                this.filteredCustomerOptions = customerOptions;
                const requestedResourceKey = this.route.snapshot.queryParamMap.get('resourceKey');
                this.selectedDefinition = this.selectedDefinition
                    ? definitions.find(item => item.workflowDefinitionId === this.selectedDefinition?.workflowDefinitionId)
                    : (requestedResourceKey ? definitions.find(item => item.resourceKey === requestedResourceKey) : undefined) || definitions[0];
                this.syncSelectionAfterLoad();
                this.isLoading = false;
                this.rebuildGraph();
                this.fitCanvasSoon();
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load workflows.' });
            }
        });
    }

    selectDefinition(definition: WorkflowDefinition): void {
        this.selectedDefinition = definition;
        this.selectedNode = undefined;
        this.selectedTransition = undefined;
        this.selectedCondition = undefined;
        this.definitionForm.reset(definition);
        this.inspectorMode = 'workflow';
        this.rebuildGraph();
        this.fitCanvasSoon();
    }

    openResource(resource?: WorkflowResourceType): void {
        this.resourceForm.reset(resource ?? { active: true });
        this.showResourceDialog = true;
    }

    saveResource(): void {
        if (!this.companyId || this.resourceForm.invalid) {
            this.resourceForm.markAllAsTouched();
            return;
        }
        this.workflowService.saveResourceType({ ...this.resourceForm.value, companyId: this.companyId }).subscribe({
            next: () => {
                this.showResourceDialog = false;
                this.load();
                this.messageService.add({ severity: 'success', summary: 'Resource saved' });
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Unable to save the resource type.' })
        });
    }

    openDefinition(definition?: WorkflowDefinition): void {
        this.selectedDefinition = definition ?? this.selectedDefinition;
        this.definitionForm.reset(definition ?? { version: 1, active: true });
        this.inspectorMode = 'workflow';
    }

    saveDefinition(): void {
        if (!this.companyId || this.definitionForm.invalid) {
            this.definitionForm.markAllAsTouched();
            return;
        }
        const payload = { ...this.definitionForm.value, companyId: this.companyId } as WorkflowDefinition;
        const request = payload.workflowDefinitionId
            ? this.workflowService.updateDefinition(payload)
            : this.workflowService.saveDefinition(payload);
        request.subscribe({
            next: definition => {
                this.selectedDefinition = definition;
                this.messageService.add({ severity: 'success', summary: 'Workflow saved' });
                this.load();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Unable to save the workflow.' })
        });
    }

    openNode(node?: WorkflowStatusNode): void {
        const position = this.nextNodePosition();
        this.selectedNode = node;
        this.selectedTransition = undefined;
        this.selectedCondition = undefined;
        this.nodeForm.reset(node ?? {
            nodeType: 'NORMAL',
            isFinal: false,
            active: true,
            positionX: position.x,
            positionY: position.y,
            sortOrder: (this.selectedDefinition?.nodes?.length || 0) + 1
        });
        this.inspectorMode = 'step';
    }

    saveNode(): void {
        if (!this.selectedDefinition?.workflowDefinitionId || this.nodeForm.invalid) {
            this.nodeForm.markAllAsTouched();
            return;
        }
        this.workflowService.saveNode(this.selectedDefinition.workflowDefinitionId, this.nodeForm.value).subscribe({
            next: node => {
                this.selectedNode = node;
                this.messageService.add({ severity: 'success', summary: 'Step saved' });
                this.load();
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message ?? 'Unable to save the step.' })
        });
    }

    deleteNode(): void {
        if (!this.selectedDefinition?.workflowDefinitionId || !this.selectedNode?.nodeId) {
            return;
        }
        const activeConnections = (this.selectedDefinition.transitions || []).filter(transition =>
            transition.active !== false &&
            (transition.fromStatusCode === this.selectedNode?.statusCode || transition.toStatusCode === this.selectedNode?.statusCode)
        );
        if (activeConnections.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Step is connected',
                detail: 'Remove actions connected to this step before deleting it.'
            });
            return;
        }
        this.workflowService.deleteNode(this.selectedDefinition.workflowDefinitionId, this.selectedNode.nodeId).subscribe({
            next: () => {
                this.selectedNode = undefined;
                this.inspectorMode = 'workflow';
                this.messageService.add({ severity: 'success', summary: 'Step removed' });
                this.load();
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: error?.error?.message ?? 'Unable to remove the step.' })
        });
    }

    openTransition(transition?: WorkflowTransition): void {
        const fromStatusCode = this.selectedNode?.statusCode ?? '';
        this.selectedTransition = transition;
        this.selectedNode = undefined;
        this.selectedCondition = undefined;
        this.transitionForm.reset(transition ? this.toTransitionFormValue(transition) : {
            fromStatusCode,
            toStatusCode: '',
            requiresComment: false,
            requiresAttachment: false,
            allowedRoleId: null,
            allowedCustomer: null,
            active: true
        });
        this.inspectorMode = 'action';
    }

    saveTransition(): void {
        if (!this.selectedDefinition?.workflowDefinitionId || this.transitionForm.invalid) {
            this.transitionForm.markAllAsTouched();
            return;
        }
        const formValue = this.transitionForm.getRawValue();
        const payload: WorkflowTransition = {
            ...this.selectedTransition,
            ...formValue,
            allowedCustomerId: formValue.allowedCustomer?.customerId ?? null,
            allowedCustomer: formValue.allowedCustomer ?? null
        };
        this.workflowService.saveTransition(this.selectedDefinition.workflowDefinitionId, payload).subscribe({
            next: transition => {
                this.selectedTransition = transition;
                this.messageService.add({ severity: 'success', summary: 'Action saved' });
                this.load();
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message ?? 'Unable to save the action.' })
        });
    }

    deleteTransition(): void {
        if (!this.selectedTransition?.transitionId) {
            return;
        }
        this.workflowService.deleteTransition(this.selectedTransition.transitionId).subscribe({
            next: () => {
                this.selectedTransition = undefined;
                this.inspectorMode = 'workflow';
                this.messageService.add({ severity: 'success', summary: 'Action removed' });
                this.load();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'Unable to remove the action.' })
        });
    }

    openCondition(transition: WorkflowTransition, condition?: WorkflowTransitionCondition): void {
        this.selectedTransition = transition;
        this.selectedCondition = condition;
        this.selectedNode = undefined;
        this.conditionForm.reset(condition ?? { conditionType: 'ALWAYS', active: true, sortOrder: (transition.conditions?.length || 0) + 1 });
        this.inspectorMode = 'condition';
    }

    saveCondition(): void {
        if (!this.selectedTransition?.transitionId || this.conditionForm.invalid) {
            this.conditionForm.markAllAsTouched();
            return;
        }
        this.workflowService.saveCondition(this.selectedTransition.transitionId, this.conditionForm.value).subscribe({
            next: condition => {
                this.selectedCondition = condition;
                this.messageService.add({ severity: 'success', summary: 'Rule saved' });
                this.load();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Unable to save the rule.' })
        });
    }

    deleteCondition(condition: WorkflowTransitionCondition): void {
        if (!condition.conditionId) {
            return;
        }
        this.workflowService.deleteCondition(condition.conditionId).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Rule removed' });
                this.load();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'Unable to remove the rule.' })
        });
    }

    onMoveNodes(event: FMoveNodesEvent): void {
        if (!this.selectedDefinition?.workflowDefinitionId || !event.fNodes.length) {
            return;
        }
        const movedByStatus = new Map(event.fNodes.map(node => [this.statusFromNodeId(node.id), node.position]));
        const layout = this.activeNodes
            .filter(node => movedByStatus.has(node.statusCode))
            .map(node => {
                const position = movedByStatus.get(node.statusCode)!;
                node.positionX = Math.round(position.x);
                node.positionY = Math.round(position.y);
                return {
                    nodeId: node.nodeId,
                    statusCode: node.statusCode,
                    positionX: node.positionX,
                    positionY: node.positionY
                };
            });
        this.workflowService.saveNodeLayout(this.selectedDefinition.workflowDefinitionId, layout).subscribe({
            next: nodes => {
                if (this.selectedDefinition) {
                    this.selectedDefinition.nodes = nodes;
                    this.rebuildGraph();
                }
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Layout failed', detail: 'Unable to save the step positions.' })
        });
    }

    onCreateConnection(event: FCreateConnectionEvent): void {
        const fromStatus = this.statusFromConnectorId(event.fOutputId);
        const toStatus = this.statusFromConnectorId(event.fInputId);
        if (!fromStatus || !toStatus || fromStatus === toStatus) {
            return;
        }
        const target = this.activeNodes.find(node => node.statusCode === toStatus);
        this.selectedNode = undefined;
        this.selectedCondition = undefined;
        this.selectedTransition = undefined;
        this.transitionForm.reset({
            fromStatusCode: fromStatus,
            toStatusCode: toStatus,
            actionCode: `MOVE_TO_${toStatus}`,
            label: target ? `Move to ${target.label}` : 'Move',
            requiresComment: false,
            requiresAttachment: false,
            allowedRoleId: null,
            allowedCustomer: null,
            active: true
        });
        this.inspectorMode = 'action';
    }

    onReassignConnection(event: FReassignConnectionEvent): void {
        if (!this.selectedDefinition?.workflowDefinitionId) {
            return;
        }
        const transitionId = Number(event.connectionId.replace('transition-', ''));
        const transition = (this.selectedDefinition.transitions || []).find(item => item.transitionId === transitionId);
        if (!transition) {
            return;
        }
        const nextTransition = {
            ...transition,
            fromStatusCode: this.statusFromConnectorId(event.newSourceId) || transition.fromStatusCode,
            toStatusCode: this.statusFromConnectorId(event.newTargetId) || transition.toStatusCode
        };
        this.workflowService.saveTransition(this.selectedDefinition.workflowDefinitionId, nextTransition).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Action updated' });
                this.load();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Unable to update the action path.' })
        });
    }

    onSelectionChange(event: FSelectionChangeEvent): void {
        const nodeId = event.fNodeIds[0];
        if (nodeId) {
            const node = this.activeNodes.find(item => this.nodeId(item) === nodeId);
            if (node) {
                this.openNode(node);
                return;
            }
        }
        const connectionId = event.fConnectionIds[0];
        if (connectionId) {
            const transition = (this.selectedDefinition?.transitions || []).find(item => this.connectionId(item) === connectionId);
            if (transition) {
                this.openTransition(transition);
            }
        }
    }

    fitCanvas(): void {
        this.canvas?.fitToScreen({ x: 120, y: 100 }, true);
    }

    resetCanvas(): void {
        this.canvas?.resetScaleAndCenter(true);
    }

    arrangeSteps(): void {
        if (!this.selectedDefinition?.workflowDefinitionId) {
            return;
        }
        const layout = this.buildAutoLayout();
        this.workflowService.saveNodeLayout(this.selectedDefinition.workflowDefinitionId, layout).subscribe({
            next: nodes => {
                if (this.selectedDefinition) {
                    this.selectedDefinition.nodes = nodes;
                    this.rebuildGraph();
                    this.fitCanvasSoon();
                    this.messageService.add({ severity: 'success', summary: 'Steps arranged' });
                }
            }
        });
    }

    nodeId(node: WorkflowStatusNode): string {
        return `node-${node.statusCode}`;
    }

    inputId(node: WorkflowStatusNode): string {
        return `${node.statusCode}__in`;
    }

    outputId(node: WorkflowStatusNode): string {
        return `${node.statusCode}__out`;
    }

    connectionId(transition: WorkflowTransition): string {
        return `transition-${transition.transitionId ?? `${transition.fromStatusCode}-${transition.actionCode}`}`;
    }

    tagSeverity(node: WorkflowStatusNode): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (node.active === false) {
            return 'secondary';
        }
        if (node.isFinal || node.nodeType === 'FINAL') {
            return 'success';
        }
        if (node.nodeType === 'START') {
            return 'info';
        }
        if (node.nodeType === 'APPROVAL') {
            return 'warn';
        }
        return 'secondary';
    }

    toTitle(value?: string | null): string {
        return (value || '')
            .toLowerCase()
            .split('_')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    filterAllowedCustomers(event: { query?: string | null }): void {
        const query = (event.query || '').trim().toLowerCase();
        if (!query) {
            this.filteredCustomerOptions = [...this.customerOptions];
            return;
        }
        this.filteredCustomerOptions = this.customerOptions.filter(option =>
            option.customerName?.toLowerCase().includes(query)
            || option.emailAddress?.toLowerCase().includes(query)
            || option.phoneNumber?.toLowerCase().includes(query)
        );
    }

    customerMeta(option?: WorkflowCustomerOption | null): string {
        if (!option) {
            return '';
        }
        return option.emailAddress || option.phoneNumber || `Customer #${option.customerId}`;
    }

    private syncSelectionAfterLoad(): void {
        if (!this.selectedDefinition) {
            this.inspectorMode = 'empty';
            return;
        }
        this.definitionForm.reset(this.selectedDefinition);
        if (this.selectedNode) {
            this.selectedNode = this.selectedDefinition.nodes?.find(node => node.nodeId === this.selectedNode?.nodeId);
            if (this.selectedNode) {
                this.nodeForm.reset(this.selectedNode);
                this.inspectorMode = 'step';
                return;
            }
        }
        if (this.selectedTransition) {
            this.selectedTransition = this.selectedDefinition.transitions?.find(transition => transition.transitionId === this.selectedTransition?.transitionId);
            if (this.selectedTransition) {
                this.transitionForm.reset(this.toTransitionFormValue(this.selectedTransition));
                this.inspectorMode = 'action';
                return;
            }
        }
        this.inspectorMode = 'workflow';
    }

    private rebuildGraph(): void {
        const nodes = this.selectedDefinition?.nodes || [];
        this.graphNodes = nodes.map((node, index) => ({
            node,
            id: this.nodeId(node),
            inputId: this.inputId(node),
            outputId: this.outputId(node),
            position: this.resolvePosition(node, index)
        }));

        const activeStatusCodes = new Set(nodes.filter(node => node.active !== false).map(node => node.statusCode));
        this.graphConnections = (this.selectedDefinition?.transitions || [])
            .filter(transition =>
                transition.active !== false &&
                activeStatusCodes.has(transition.fromStatusCode) &&
                activeStatusCodes.has(transition.toStatusCode)
            )
            .map(transition => ({
                transition,
                id: this.connectionId(transition),
                outputId: `${transition.fromStatusCode}__out`,
                inputId: `${transition.toStatusCode}__in`
            }));
        this.validationMessages = this.validateDefinition();
    }

    private validateDefinition(): ValidationMessage[] {
        if (!this.selectedDefinition) {
            return [];
        }
        const messages: ValidationMessage[] = [];
        const nodes = this.activeNodes;
        const statusCodes = new Set(nodes.map(node => node.statusCode));
        const transitions = (this.selectedDefinition.transitions || []).filter(transition => transition.active !== false);

        if (!statusCodes.has(this.selectedDefinition.initialStatusCode)) {
            messages.push({ severity: 'error', text: 'The first step does not exist in this workflow.' });
        }
        if (!nodes.some(node => node.nodeType === 'START')) {
            messages.push({ severity: 'error', text: 'Add one Start step so users know where the workflow begins.' });
        }
        for (const transition of transitions) {
            if (!statusCodes.has(transition.fromStatusCode) || !statusCodes.has(transition.toStatusCode)) {
                messages.push({ severity: 'error', text: `${transition.label} points to a missing or inactive step.` });
            }
            if (!transition.allowedRoleId && !transition.allowedCustomerId) {
                messages.push({ severity: 'warn', text: `${transition.label} does not have a role or specific customer binding yet.` });
            }
        }
        const finalStatuses = new Set(nodes.filter(node => node.isFinal || node.nodeType === 'FINAL').map(node => node.statusCode));
        for (const transition of transitions.filter(item => finalStatuses.has(item.fromStatusCode))) {
            messages.push({ severity: 'warn', text: `${transition.label} starts from a final step. Final steps should close the workflow.` });
        }
        const seen = new Set<string>();
        for (const transition of transitions) {
            const key = `${transition.fromStatusCode}::${transition.actionCode}`;
            if (seen.has(key)) {
                messages.push({ severity: 'error', text: `${transition.fromStatusCode} has duplicate action code ${transition.actionCode}.` });
            }
            seen.add(key);
        }
        return messages;
    }

    private resolvePosition(node: WorkflowStatusNode, index: number): { x: number; y: number } {
        if (node.positionX !== null && node.positionX !== undefined && node.positionY !== null && node.positionY !== undefined) {
            return { x: node.positionX, y: node.positionY };
        }
        return this.defaultPosition(index);
    }

    private defaultPosition(index: number): { x: number; y: number } {
        const column = index % this.layoutGridColumns;
        const row = Math.floor(index / this.layoutGridColumns);
        return {
            x: this.layoutStartX + column * this.layoutColumnWidth,
            y: this.layoutStartY + row * this.layoutRowHeight
        };
    }

    private nextNodePosition(): { x: number; y: number } {
        return this.defaultPosition(this.selectedDefinition?.nodes?.length || 0);
    }

    private statusFromNodeId(id?: string): string {
        return (id || '').replace(/^node-/, '');
    }

    private statusFromConnectorId(id?: string): string {
        return (id || '').replace(/__(in|out)$/, '');
    }

    private fitCanvasSoon(): void {
        setTimeout(() => this.fitCanvas(), 100);
    }

    private buildAutoLayout(): Array<{ nodeId: number | null; statusCode: string; positionX: number; positionY: number; }> {
        const nodes = [...this.activeNodes].sort((left, right) => this.compareNodesForLayout(left, right));
        const nodeByStatus = new Map(nodes.map(node => [node.statusCode, node] as const));
        const transitions = (this.selectedDefinition?.transitions || []).filter(transition =>
            transition.active !== false &&
            nodeByStatus.has(transition.fromStatusCode) &&
            nodeByStatus.has(transition.toStatusCode)
        );

        const incomingByStatus = new Map<string, Set<string>>();
        const outgoingByStatus = new Map<string, Set<string>>();
        for (const node of nodes) {
            incomingByStatus.set(node.statusCode, new Set<string>());
            outgoingByStatus.set(node.statusCode, new Set<string>());
        }
        for (const transition of transitions) {
            incomingByStatus.get(transition.toStatusCode)?.add(transition.fromStatusCode);
            outgoingByStatus.get(transition.fromStatusCode)?.add(transition.toStatusCode);
        }

        const layerByStatus = new Map<string, number>();
        const queue: string[] = [];
        const initialStatusCode = this.selectedDefinition?.initialStatusCode;
        if (initialStatusCode && nodeByStatus.has(initialStatusCode)) {
            layerByStatus.set(initialStatusCode, 0);
            queue.push(initialStatusCode);
        }

        for (const node of nodes) {
            const hasNoIncoming = (incomingByStatus.get(node.statusCode)?.size || 0) === 0;
            if (hasNoIncoming && !layerByStatus.has(node.statusCode)) {
                layerByStatus.set(node.statusCode, 0);
                queue.push(node.statusCode);
            }
        }

        while (queue.length) {
            const currentStatus = queue.shift()!;
            const currentLayer = layerByStatus.get(currentStatus) ?? 0;
            const nextStatuses = [...(outgoingByStatus.get(currentStatus) || [])].sort();
            for (const nextStatus of nextStatuses) {
                if (!layerByStatus.has(nextStatus)) {
                    layerByStatus.set(nextStatus, currentLayer + 1);
                    queue.push(nextStatus);
                }
            }
        }

        let fallbackLayer = Math.max(0, ...layerByStatus.values());
        for (const node of nodes) {
            if (!layerByStatus.has(node.statusCode)) {
                fallbackLayer += 1;
                layerByStatus.set(node.statusCode, fallbackLayer);
            }
        }

        const nodesByLayer = new Map<number, WorkflowStatusNode[]>();
        for (const node of nodes) {
            const layer = layerByStatus.get(node.statusCode) ?? 0;
            const bucket = nodesByLayer.get(layer) || [];
            bucket.push(node);
            nodesByLayer.set(layer, bucket);
        }

        const layerEntries = [...nodesByLayer.entries()]
            .sort((left, right) => left[0] - right[0])
            .map(([layer, layerNodes]) => [layer, layerNodes.sort((left, right) => this.compareNodesForLayout(left, right))] as const);
        const maxLayerSize = Math.max(1, ...layerEntries.map(([, layerNodes]) => layerNodes.length));

        return layerEntries.flatMap(([layer, layerNodes]) => {
            const offsetRows = (maxLayerSize - layerNodes.length) / 2;
            return layerNodes.map((node, index) => ({
                nodeId: node.nodeId ?? null,
                statusCode: node.statusCode,
                positionX: Math.round(this.layoutStartX + layer * this.layoutColumnWidth),
                positionY: Math.round(this.layoutStartY + (offsetRows + index) * this.layoutRowHeight)
            }));
        });
    }

    private compareNodesForLayout(left: WorkflowStatusNode, right: WorkflowStatusNode): number {
        const nodeTypeRank = (value?: string | null): number => {
            switch (value) {
                case 'START':
                    return 0;
                case 'APPROVAL':
                    return 1;
                case 'NORMAL':
                    return 2;
                case 'FINAL':
                    return 3;
                default:
                    return 4;
            }
        };

        return (left.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right.sortOrder ?? Number.MAX_SAFE_INTEGER)
            || nodeTypeRank(left.nodeType) - nodeTypeRank(right.nodeType)
            || left.label.localeCompare(right.label)
            || left.statusCode.localeCompare(right.statusCode);
    }

    private resolveReviewTab(tab: string | null): 'steps' | 'actions' | 'checks' {
        return (this.reviewTabKeys.find(value => value === tab) || 'steps') as 'steps' | 'actions' | 'checks';
    }

    private toTransitionFormValue(transition: WorkflowTransition) {
        return {
            ...transition,
            allowedCustomer: transition.allowedCustomer
                ?? this.customerOptions.find(option => option.customerId === transition.allowedCustomerId)
                ?? null
        };
    }
}
